import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "../../layout/AppLayout";
import { Alert, ErpSelect, ErpTable, Modal, toOptions } from "../../components/erp";
import { errorText } from "../../api/client";
import { ppDropdownsApi, ReceivalBreakupRow, receivalApi } from "./api";

const today = () => new Date().toISOString().slice(0, 10);

const BreakupModal: React.FC<{ row: ReceivalBreakupRow; receivingId?: number; onClose: () => void; onSaved: () => void }> = ({ row, receivingId, onClose, onSaved }) => {
  const [v, setV] = useState({ SampleQuantity: String(row.SampleQuantity ?? ""), NoofPieces: String(row.NoofPieces ?? ""),
    ReceivingCount: String(row.ReceivingCount ?? ""), ReceivingQty: String(row.ReceivingQty ?? "") });
  const [err, setErr] = useState<string | null>(null); const [saving, setSaving] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setErr(null);
    try {
      const r = await receivalApi.post({ BreakUpdate: true, ReceivalBreakupID: row.ReceivalBreakupID || "", ReceivingID: receivingId,
        ArrivalID: row.ArrivalID, ArrivalBreakupID: row.ArrivalBreakupID, ...v });
      if (r.Error) setErr(r.Error); else onSaved();
    } catch (ex) { setErr(errorText(ex)); } finally { setSaving(false); }
  };
  return (
    <Modal open title={`Receiving — ${row.ArrivalNumber}`} onClose={onClose} testId="receival-breakup-modal">
      <form onSubmit={submit} data-testid="receival-breakup-form">
        {(["SampleQuantity", "NoofPieces", "ReceivingCount", "ReceivingQty"] as const).map((k) => (
          <div className="mb-3" key={k}><label className="form-label">{k}</label>
            <input className="form-control" value={v[k]} onChange={(e) => setV((s) => ({ ...s, [k]: e.target.value }))} data-testid={`receival-breakup-${k}`} /></div>
        ))}
        {err && <Alert kind="danger" testId="receival-breakup-error">{err}</Alert>}
        <button className="btn btn-primary" type="submit" disabled={saving} data-testid="receival-breakup-submit">{saving ? "Saving..." : "Save"}</button>
      </form>
    </Modal>
  );
};

/** Port of v3/PreProcessing/receival-page-trackid.php — entry point of the PreProcessing chain: receives a
 * Purchase vehicle arrival at a plant and generates the TrackID (ReceivingID) every downstream stage uses. */
export default function ReceivalPage() {
  const [params, setParams] = useSearchParams();
  const arrivalDate = params.get("ArrivalDate") || today();
  const vehicleId = params.get("VehicleID") || "";
  const plantId = params.get("ReceivingPlantID") || "";
  const qc = useQueryClient();

  const dd = useQuery({ queryKey: ["pp", "dropdowns"], queryFn: ppDropdownsApi.get, staleTime: 5 * 60_000 });
  const detail = useQuery({ queryKey: ["pp", "receival", arrivalDate, vehicleId, plantId], enabled: !!(arrivalDate && vehicleId && plantId),
    queryFn: () => receivalApi.get({ ArrivalDate: arrivalDate, VehicleID: vehicleId, ReceivingPlantID: plantId }) });

  const [vehicleNumber, setVehicleNumber] = useState("");
  const [sealNo, setSealNo] = useState(""); const [supervisorId, setSupervisorId] = useState(""); const [remarks, setRemarks] = useState("");
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null); const [saving, setSaving] = useState(false);
  const [editRow, setEditRow] = useState<ReceivalBreakupRow | null>(null);

  const header = detail.data?.Data?.RmReceivingDetails;
  const rows = detail.data?.Data?.RmReceivingBreakUp || [];
  const totals = detail.data?.Data?.Totals?.[0];
  const supervisors = useMemo(() => toOptions(dd.data?.Data?.Supervisor, (s) => s.ID, (s) => s.UserName), [dd.data]);
  const plants = useMemo(() => toOptions(dd.data?.Data?.Plant, (p) => p.ProcessingStationID, (p) => p.StationName), [dd.data]);

  const submitHeader = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setMsg(null);
    try {
      const r = await receivalApi.post({ ArrivalDate: arrivalDate, VehicleID: vehicleId, VehicleNumber: vehicleNumber,
        ReceivingPlantID: plantId, SealNo: sealNo, SupervisorID: supervisorId, Remarks: remarks });
      if (r.Error) setMsg({ ok: false, text: r.Error }); else { setMsg({ ok: true, text: r.Msg || "Saved" }); qc.invalidateQueries({ queryKey: ["pp", "receival"] }); }
    } catch (ex) { setMsg({ ok: false, text: errorText(ex) }); } finally { setSaving(false); }
  };

  return (
    <>
      <PageHeader title="Receival" location="Preprocessing" />
      <div className="card mb-3"><div className="card-body">
        <form onSubmit={submitHeader} data-testid="receival-header-form">
          <div className="row">
            <div className="form-group col-md-3 mb-3"><label className="form-label">Arrival Date</label>
              <input className="form-control" type="date" value={arrivalDate} onChange={(e) => setParams((p) => { p.set("ArrivalDate", e.target.value); return p; })} data-testid="receival-arrival-date" /></div>
            <div className="form-group col-md-3 mb-3"><label className="form-label">Vehicle ID</label>
              <input className="form-control" value={vehicleId} onChange={(e) => setParams((p) => { p.set("VehicleID", e.target.value); return p; })} data-testid="receival-vehicle-id" /></div>
            <div className="form-group col-md-3 mb-3"><label className="form-label">Vehicle Number</label>
              <input className="form-control" value={vehicleNumber} onChange={(e) => setVehicleNumber(e.target.value)} data-testid="receival-vehicle-number" /></div>
            <div className="form-group col-md-3 mb-3"><label className="form-label">Plant</label>
              <ErpSelect id="receival-plant" value={plantId} onChange={(v) => setParams((p) => { p.set("ReceivingPlantID", v); return p; })} options={plants} placeholder="Select Plant" testId="receival-plant" /></div>
            <div className="form-group col-md-3 mb-3"><label className="form-label">Seal No</label>
              <input className="form-control" value={sealNo} onChange={(e) => setSealNo(e.target.value)} data-testid="receival-seal-no" /></div>
            <div className="form-group col-md-3 mb-3"><label className="form-label">Supervisor</label>
              <ErpSelect id="receival-supervisor" value={supervisorId} onChange={setSupervisorId} options={supervisors} placeholder="Select Supervisor" testId="receival-supervisor" /></div>
            <div className="form-group col-md-6 mb-3"><label className="form-label">Remarks</label>
              <input className="form-control" value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="receival-remarks" /></div>
          </div>
          {header?.TrackCode && <p className="mb-2">Track Code: <strong data-testid="receival-track-code">{header.TrackCode}</strong></p>}
          {msg && <Alert kind={msg.ok ? "success" : "danger"} testId="receival-msg">{msg.text}</Alert>}
          <button className="btn btn-primary" type="submit" disabled={saving} data-testid="receival-submit">{saving ? "Saving..." : "Save Receival"}</button>
        </form>
      </div></div>

      <div className="card"><div className="card-body">
        <h6 className="mb-3">Arrival Breakup</h6>
        <ErpTable<ReceivalBreakupRow> testId="receival-breakup-table" loading={detail.isLoading}
          error={detail.isError ? errorText(detail.error) : null} rows={rows}
          columns={[{ key: "ArrivalNumber", title: "Arrival No" }, { key: "ArrivalCount", title: "Arrival Count" },
            { key: "ArrivalWeight", title: "Arrival Weight" }, { key: "ArrivalVariety", title: "Variety" },
            { key: "SampleQuantity", title: "Sample Qty" }, { key: "ReceivingCount", title: "Receiving Count" },
            { key: "ReceivingQty", title: "Receiving Qty" }, { key: "CountDifference", title: "Count Diff" },
            { key: "QuantityDifference", title: "Qty Diff" },
            { key: "Action", title: "Action", render: (r) => <button className="btn btn-sm btn-outline-primary" data-testid={`receival-edit-${r.ArrivalBreakupID}`} onClick={() => setEditRow(r)}>Edit</button> }]}
          footer={totals && <tr><td colSpan={2}><strong>Totals</strong></td><td>{totals.TotalWeight}</td><td /><td /><td /><td>{totals.TotalRecvQty}</td><td /><td>{totals.QtyDiff}</td><td /></tr>} />
      </div></div>
      {editRow && <BreakupModal row={editRow} receivingId={header?.ReceivingID} onClose={() => setEditRow(null)}
        onSaved={() => { setEditRow(null); qc.invalidateQueries({ queryKey: ["pp", "receival"] }); }} />}
    </>
  );
}
